/**
 * Modul de citire cub Zarr direct în browser cu zarrita.
 * Citește întregul cub pentru o variabilă (AOI mic, ~1-2 MB per variabilă).
 */
import * as zarr from 'zarrita';

// zarrita FetchStore necesită URL absolut
import { urlStore, sursaCurenta } from './data-source.js';

// Sursa activă (CAMS sau modelul național) se alege din comutatorul din panou
// și se reține între sesiuni; vezi data-source.js.
const ZARR_STORE_URL = urlStore('surface');

/** Cache per variabilă — evită re-descărcarea */
const cubeCache = new Map();

/** Metadate store (run_date, etc.) */
let storeAttrs = null;

/**
 * Citește metadatele store-ului (.zattrs).
 */
export async function loadStoreMetadata() {
  if (storeAttrs) return storeAttrs;

  const store = new zarr.FetchStore(ZARR_STORE_URL);
  const root = await zarr.open(store, { kind: 'group' });
  storeAttrs = root.attrs;
  return storeAttrs;
}

/**
 * Încarcă cubul complet pentru o variabilă.
 * @param {string} variable - Numele variabilei canonice (ex. 'pm25')
 * @returns {{ data: Float32Array|Float64Array, lats: Float64Array, lons: Float64Array, times: BigInt64Array|Float64Array, shape: [number,number,number], attrs: object }}
 */
export async function loadCube(variable) {
  if (cubeCache.has(variable)) {
    return cubeCache.get(variable);
  }

  const store = new zarr.FetchStore(ZARR_STORE_URL);
  const root = await zarr.open(store, { kind: 'group' });

  // Deschide array-urile
  const arr = await zarr.open(root.resolve(variable), { kind: 'array' });
  const latArr = await zarr.open(root.resolve('latitude'), { kind: 'array' });
  const lonArr = await zarr.open(root.resolve('longitude'), { kind: 'array' });
  const timeArr = await zarr.open(root.resolve('time'), { kind: 'array' });

  // Citește totul
  const [data, lats, lons, times] = await Promise.all([
    zarr.get(arr),
    zarr.get(latArr),
    zarr.get(lonArr),
    zarr.get(timeArr),
  ]);

  const cube = {
    data: data.data,
    lats: lats.data,
    lons: lons.data,
    times: times.data,
    shape: data.shape,
    attrs: arr.attrs || {},
    timeAttrs: timeArr.attrs || {},
  };

  cubeCache.set(variable, cube);
  return cube;
}

/**
 * Extrage seria temporală pentru un pixel.
 * @param {{ data: TypedArray, shape: number[] }} cube
 * @param {number} latIdx - Indexul latitudine
 * @param {number} lonIdx - Indexul longitudine
 * @returns {number[]} - Valorile pe toți timestep-urile
 */
export function extractTimeSeries(cube, latIdx, lonIdx) {
  const [nt, ny, nx] = cube.shape;
  const series = new Array(nt);
  for (let t = 0; t < nt; t++) {
    const v = cube.data[t * ny * nx + latIdx * nx + lonIdx];
    series[t] = Number.isNaN(v) ? null : v;
  }
  return series;
}

/**
 * Găsește cel mai apropiat index într-un array sortat.
 */
export function nearestIndex(arr, value) {
  let best = 0;
  let bestDist = Infinity;
  for (let k = 0; k < arr.length; k++) {
    const d = Math.abs(Number(arr[k]) - value);
    if (d < bestDist) {
      bestDist = d;
      best = k;
    }
  }
  return best;
}

/**
 * Convertește timestamps din Zarr în Date objects.
 *
 * zarrita NU decodifică CF-conventions — valorile sunt numere brute
 * (ex. ore de la o dată de bază). Trebuie să citim .zattrs[units]
 * pentru a obține baza (ex. "hours since 2026-06-17 00:00:00").
 *
 * @param {TypedArray} times - Valorile brute ale axei de timp
 * @param {object} [timeAttrs] - Atributele din time/.zattrs (opțional)
 * @returns {Date[]}
 */
export function timesToDates(times, timeAttrs = null) {
  // Încercare decodare CF: "hours since YYYY-MM-DD HH:MM:SS"
  if (timeAttrs?.units) {
    const match = timeAttrs.units.match(
      /^(\w+)\s+since\s+(\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}:\d{2})?)/
    );
    if (match) {
      const unit = match[1].toLowerCase();
      const baseDate = new Date(match[2].replace(' ', 'T') + 'Z');
      const basMs = baseDate.getTime();

      const multipliers = {
        seconds: 1000,
        second: 1000,
        minutes: 60 * 1000,
        minute: 60 * 1000,
        hours: 3600 * 1000,
        hour: 3600 * 1000,
        days: 86400 * 1000,
        day: 86400 * 1000,
      };
      const mult = multipliers[unit] || 1000;

      return Array.from(times, t => new Date(basMs + Number(t) * mult));
    }
  }

  // Fallback: treat as nanoseconds since epoch (BigInt or Number)
  return Array.from(times, t => {
    if (typeof t === 'bigint') {
      return new Date(Number(t / 1000000n));
    }
    const ms = Number(t);
    if (Math.abs(ms) > 1e15) return new Date(ms / 1e6);
    if (Math.abs(ms) > 1e10) return new Date(ms);
    return new Date(ms * 1000);
  });
}

/**
 * Returnează lista variabilelor disponibile în store.
 */
export async function getAvailableVariables() {
  // Returnăm lista hardcoded — variabilele sunt cunoscute
  // și evităm un fetch suplimentar
  return ['no2', 'o3', 'pm25', 'pm10', 'so2', 'co'];
}

// ── Weather Store (ECMWF Open Data) ────────────────────────────────
const WEATHER_STORE_URL = `${window.location.origin}${import.meta.env.BASE_URL}data/zarr/weather_ro_latest.zarr`;
const weatherCache = new Map();
let weatherStoreAttrs = null;
let weatherAvailable = null; // null = necunoscut, true/false

/**
 * Verifică dacă store-ul meteo este disponibil.
 */
export async function isWeatherAvailable() {
  if (weatherAvailable !== null) return weatherAvailable;
  try {
    const res = await fetch(WEATHER_STORE_URL + '/.zgroup', { method: 'HEAD' });
    weatherAvailable = res.ok;
  } catch {
    weatherAvailable = false;
  }
  return weatherAvailable;
}

/**
 * Citește metadatele store-ului meteo.
 */
export async function loadWeatherMetadata() {
  if (weatherStoreAttrs) return weatherStoreAttrs;
  const store = new zarr.FetchStore(WEATHER_STORE_URL);
  const root = await zarr.open(store, { kind: 'group' });
  weatherStoreAttrs = root.attrs;
  return weatherStoreAttrs;
}

/**
 * Încarcă cubul complet pentru o variabilă meteo.
 */
export async function loadWeatherCube(variable) {
  const cacheKey = `wx_${variable}`;
  if (weatherCache.has(cacheKey)) return weatherCache.get(cacheKey);

  const store = new zarr.FetchStore(WEATHER_STORE_URL);
  const root = await zarr.open(store, { kind: 'group' });

  const arr = await zarr.open(root.resolve(variable), { kind: 'array' });
  const latArr = await zarr.open(root.resolve('latitude'), { kind: 'array' });
  const lonArr = await zarr.open(root.resolve('longitude'), { kind: 'array' });
  const timeArr = await zarr.open(root.resolve('time'), { kind: 'array' });

  const [data, lats, lons, times] = await Promise.all([
    zarr.get(arr), zarr.get(latArr), zarr.get(lonArr), zarr.get(timeArr),
  ]);

  const cube = {
    data: data.data,
    lats: lats.data,
    lons: lons.data,
    times: times.data,
    shape: data.shape,
    attrs: arr.attrs || {},
    timeAttrs: timeArr.attrs || {},
  };

  weatherCache.set(cacheKey, cube);
  return cube;
}

/**
 * Încarcă componentele U și V ale vântului simultan.
 * @returns {{ u: cube, v: cube }}
 */
export async function loadWindField() {
  const [u, v] = await Promise.all([
    loadWeatherCube('wind_u'),
    loadWeatherCube('wind_v'),
  ]);
  return { u, v };
}

/**
 * Extrage U și V ca flat arrays pentru un timestep specific.
 * @param {{ u: cube, v: cube }} windCubes
 * @param {number} timeIdx - Indexul de timp în cubul meteo
 * @returns {{ uData: Float32Array, vData: Float32Array, shape: [ny, nx] }}
 */
export function getWindSlice(windCubes, timeIdx) {
  const { u, v } = windCubes;
  const [nt, ny, nx] = u.shape;
  const ti = Math.max(0, Math.min(timeIdx, nt - 1));
  const offset = ti * ny * nx;
  const uSlice = u.data.slice(offset, offset + ny * nx);
  const vSlice = v.data.slice(offset, offset + ny * nx);
  return {
    uData: uSlice,
    vData: vSlice,
    lats: u.lats,
    lons: u.lons,
    shape: [ny, nx],
  };
}

/**
 * Găsește indexul de timp cel mai apropiat de momentul curent (acum).
 * @param {Date[]} dates - Array-ul de date (din timesToDates)
 * @returns {number} - Indexul cel mai apropiat de Date.now()
 */
export function findClosestTimeIndex(dates) {
  if (!dates || dates.length === 0) return 0;

  const now = Date.now();
  let bestIdx = 0;
  let bestDist = Infinity;

  for (let i = 0; i < dates.length; i++) {
    const dist = Math.abs(dates[i].getTime() - now);
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/**
 * Mapează un index de timp CAMS (orar) la cel mai apropiat index meteo (3-orar).
 * CAMS: 0, 1, 2, ..., 96  (97 pași)
 * Meteo: 0, 3, 6, ..., 96  (33 pași)
 */
export function camsToWeatherTimeIndex(camsTimeIdx) {
  // Aproximare: hora CAMS / 3 → indexul meteo
  const hour = camsTimeIdx; // CAMS e la pas orar, deci index ≈ oră
  return Math.round(hour / 3);
}
